/**
 * Meilensteine — Übersicht in der Form der Vorlage.
 *
 * Datumszeile, Titel in 46, das Zahlenband über der Tintenlinie, das
 * Zeitband mit Monatsachse und Bahnen, darunter die Liste „Alle nach Frist".
 *
 * **Der Ausschnitt folgt der Aufplanung.** Eine Bahn spannt von der frühesten
 * Frist der aufgeplanten Aufgaben eines Meilensteins bis zu seiner eigenen
 * Frist; das Fenster darüber ist die Klammer um alle Bahnen, auf ganze Monate
 * gerundet (`berechneZeitstrahlFenster`). Vorher begann jede Bahn am
 * Anlagedatum des Meilensteins — das ist eine Verwaltungsspur und kein Plan,
 * und ein im Januar angelegter Meilenstein mit Aufgaben im Oktober füllte das
 * halbe Jahr davor mit Balken, in dem niemand etwas vorhatte.
 *
 * Wer einen festen Ausschnitt will, stellt ihn im Reiter Einstellungen ein
 * (`zeitstrahl_zeitraum`, Bereich Projekt). Die Füllung der Bahnen bleibt der
 * echte Fortschritt aus `meilensteine.fortschritt`. Keine Schätzung, keine
 * Prognose: Was die Daten nicht hergeben, steht nicht da.
 */

import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ZEITSTRAHL_ZEITRAUM_LABEL,
  ZEITSTRAHL_ZEITRAUM_SCHLUESSEL,
  berechneZeitstrahlFenster,
  istZeitstrahlZeitraum,
  monatsAchse,
  positionImFenster,
  spanneImFenster,
  type Zeitfenster,
} from "@cubic/shared";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zeitband, type Zeitbahn } from "@/components/entwurf/Zeitband";
import { leererFilter } from "@/lib/filter";
import {
  fehlerMeldung,
  useAufgabenFuerProjekte,
  useEinstellungImProjekt,
  useMeilensteinAnlegen,
  useMeilensteinFortschritt,
  useMeilensteine,
  useProjekt,
} from "@/lib/haken";
import { cx, focusRing } from "@/lib/utils";

const MONATSNAME = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
] as const;

function tageBis(wert: string | Date | null | undefined): number | null {
  if (!wert) return null;
  return Math.max(
    0,
    Math.ceil(
      (new Date(wert).getTime() - Date.now()) / (24 * 60 * 60 * 1_000),
    ),
  );
}

/**
 * Die Planspanne einer Bahn: von der frühesten Aufgabenfrist bis zur Frist
 * des Meilensteins.
 */
export type Meilensteinspanne = {
  id: string;
  nummer: number;
  von: Date;
  bis: Date;
};

/**
 * Aus Meilensteinen und Aufgaben die Bahnen des Zeitbands.
 *
 * Gezählt wird die **früheste Frist einer aufgeplanten Aufgabe** je
 * Meilenstein — Aufgaben ohne Frist sind nicht aufgeplant und sagen über den
 * Zeitraum nichts; archivierte ebenso wenig.
 *
 * Ohne aufgeplante Aufgabe bleibt das Anlagedatum als einziger Anfang übrig —
 * dann ist die Bahn ehrlich leer statt erfunden. Liegt die früheste
 * Aufgabenfrist **nach** der Meilensteinfrist, beginnt die Bahn an der Frist
 * statt rückwärts zu laufen: Ein negativer Balken wäre keine Aussage.
 */
export function planspannen(
  meilensteine: readonly {
    id: string;
    nummer: number;
    angelegtAm: string | Date | null;
    faelligAm: string | Date;
  }[],
  aufgaben: readonly {
    meilensteinId?: string | null;
    faelligAm?: string | Date | null;
    archiviertAm?: string | Date | null;
  }[],
): Meilensteinspanne[] {
  const fruehesteAufgabe = new Map<string, number>();
  for (const a of aufgaben) {
    if (a.archiviertAm || !a.meilensteinId || !a.faelligAm) continue;
    const zeit = new Date(a.faelligAm).getTime();
    if (!Number.isFinite(zeit)) continue;
    const bisher = fruehesteAufgabe.get(a.meilensteinId);
    if (bisher === undefined || zeit < bisher) {
      fruehesteAufgabe.set(a.meilensteinId, zeit);
    }
  }

  return meilensteine.map((m) => {
    const bis = new Date(m.faelligAm);
    const geplant = fruehesteAufgabe.get(m.id);
    const von =
      geplant !== undefined
        ? new Date(Math.min(geplant, bis.getTime()))
        : m.angelegtAm
          ? new Date(m.angelegtAm)
          : bis;
    return { id: m.id, nummer: m.nummer, von, bis };
  });
}

/**
 * Beschriftung einer Monatszelle. Das Jahr steht an der ersten Zelle und an
 * jedem Januar — ohne das läse sich ein Fenster über den Jahreswechsel als
 * „… Dez Jan …" und verschwiege, dass dazwischen ein Jahr endet.
 */
function monatsLabel(
  zelle: { jahr: number; monat: number },
  stelle: number,
): string {
  const name = MONATSNAME[zelle.monat] ?? "";
  return stelle === 0 || zelle.monat === 0
    ? `${name} ${String(zelle.jahr).slice(-2)}`
    : name;
}

/** „Aug 26 – Feb 27" — der Ausschnitt in Worten, unter dem Band. */
function fensterText(fenster: Zeitfenster): string {
  const zellen = monatsAchse(fenster);
  const erste = zellen[0];
  const letzte = zellen[zellen.length - 1];
  if (!erste || !letzte) return "";
  return `${monatsLabel(erste, 0)} – ${monatsLabel(letzte, 0)}`;
}

function MeilensteinZeile({
  meilenstein,
  projektId,
}: {
  meilenstein: { id: string; nummer: number; name: string; faelligAm: string | Date };
  projektId: string;
}) {
  const f = useMeilensteinFortschritt(meilenstein.id);
  const wert =
    f.data && f.data.gesamt > 0
      ? Math.round((f.data.erledigt / f.data.gesamt) * 100)
      : 0;
  const tageM = tageBis(meilenstein.faelligAm);
  const ueberfaellig = tageM === 0;
  return (
    <Link
      to="/projekte/$projektId/meilensteine/$meilensteinId"
      params={{ projektId, meilensteinId: meilenstein.id }}
      className={cx(
        "flex h-[52px] cursor-pointer items-center gap-3.5 border-b border-linie transition-colors duration-150 ease-aus hover:bg-wisch",
        focusRing,
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "size-[7px] flex-none",
          ueberfaellig ? "bg-akzent" : "bg-tinte3",
        )}
      />
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-normal text-tinte">
        {meilenstein.name}
      </span>
      <Mikrolabel grad={10.5} className="flex-none">
        MS-{meilenstein.nummer} ·{" "}
        {f.data
          ? `${f.data.erledigt} von ${f.data.gesamt} erledigt`
          : "lädt …"}
      </Mikrolabel>
      <span className="hidden w-[110px] flex-none sm:block">
        <span
          className="block h-[6px] w-full bg-linie2"
          aria-label={`${wert}% erledigt`}
        >
          <span
            className="block h-full bg-tinte"
            style={{ width: `${wert}%` }}
          />
        </span>
      </span>
      <Mikrolabel grad={10.5} zahl className="w-[58px] flex-none text-right">
        {ueberfaellig
          ? "überfällig"
          : tageM != null
            ? `in ${tageM} Tagen`
            : "—"}
      </Mikrolabel>
      <span className="text-[13px] text-tinte4" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

export function MeilensteinZeitband({
  spannen,
  fenster,
}: {
  spannen: readonly Meilensteinspanne[];
  fenster: Zeitfenster;
}) {
  const achse = useMemo(
    () => monatsAchse(fenster).map(monatsLabel),
    [fenster],
  );

  const bahnen: Zeitbahn[] = spannen.map((s) => ({
    name: `MS-${s.nummer}`,
    links: positionImFenster(s.von, fenster),
    breite: spanneImFenster(s.von, s.bis, fenster),
    fuellung: "0%",
    grund: "var(--wisch)",
    fuell: "var(--tinte)",
    fuellungInhalt: <BahnFuellung id={s.id} />,
  }));

  return (
    <Zeitband
      achse={achse}
      bahnen={bahnen}
      heute={positionImFenster(new Date(), fenster)}
    />
  );
}

/** Füllung einer Bahn — lädt den echten Fortschritt des Meilensteins. */
function BahnFuellung({ id }: { id: string }) {
  const f = useMeilensteinFortschritt(id);
  const wert =
    f.data && f.data.gesamt > 0
      ? Math.round((f.data.erledigt / f.data.gesamt) * 100)
      : 0;
  return (
    <span className="block h-full bg-tinte" style={{ width: `${wert}%` }} />
  );
}

export function MeilensteineReiter({ projektId }: { projektId: string }) {
  const projekt = useProjekt(projektId);
  const meilensteine = useMeilensteine(projektId);
  const aufgaben = useAufgabenFuerProjekte([projektId], leererFilter());
  const zeitraumEinstellung = useEinstellungImProjekt(
    ZEITSTRAHL_ZEITRAUM_SCHLUESSEL,
    projektId,
  );
  const [dialogOffen, setzeDialogOffen] = useState(false);
  const [name, setzeName] = useState("");
  const [faelligAm, setzeFaelligAm] = useState("");
  const [beschreibung, setzeBeschreibung] = useState("");
  const [fehler, setzeFehler] = useState<ReturnType<typeof fehlerMeldung> | null>(null);
  const anlegen = useMeilensteinAnlegen();

  const liste = meilensteine.data ?? [];
  const ohneArchiv = useMemo(
    () => liste.filter((m) => !m.archiviertAm),
    [liste],
  );

  /*
   * Alle Merker stehen **vor** dem Wächter.
   *
   * Standen sie darunter, rief die Komponente im Ladezustand weniger Haken
   * auf als danach — React brach den Meilensteinbereich beim ersten Datensatz
   * mit „Rendered more hooks than during the previous render" ab.
   */

  const spannen = useMemo(
    () => planspannen(ohneArchiv, aufgaben.data ?? []),
    [aufgaben.data, ohneArchiv],
  );

  /*
   * Der gezeigte Ausschnitt. „automatisch" ist die Klammer um alle Bahnen,
   * eine feste Spanne zeigt stattdessen immer gleich viele Monate ab dem
   * laufenden. Ein unbekannter Wert — etwa aus einer älteren Fassung — fällt
   * auf „automatisch" zurück, statt das Band verschwinden zu lassen.
   */
  const zeitraum = istZeitstrahlZeitraum(zeitraumEinstellung.data)
    ? zeitraumEinstellung.data
    : "automatisch";
  const fenster = useMemo(
    () =>
      berechneZeitstrahlFenster(
        zeitraum,
        spannen.flatMap((s) => [s.von, s.bis]),
        new Date(),
      ),
    [zeitraum, spannen],
  );

  /* Nächster fälliger Meilenstein für die Leitzahl. */
  const naechster = useMemo(
    () =>
      liste
        .filter((m) => !m.archiviertAm)
        .sort(
          (a, b) =>
            new Date(a.faelligAm).getTime() - new Date(b.faelligAm).getTime(),
        )[0] ?? null,
    [liste],
  );

  if (projekt.isLoading || meilensteine.isLoading) {
    return <Mikrolabel grad={12}>Lade Meilensteine …</Mikrolabel>;
  }
  const p = projekt.data;

  const tage = naechster ? tageBis(naechster.faelligAm) : null;
  const laufend = liste.filter(
    (m) =>
      !m.archiviertAm &&
      new Date(m.faelligAm).getTime() >= Date.now() - 24 * 60 * 60 * 1_000,
  ).length;

  async function speichern() {
    setzeFehler(null);
    if (!p || !name.trim() || !faelligAm) return;
    try {
      await anlegen.mutateAsync({
        projektId,
        name: name.trim(),
        faelligAm: new Date(faelligAm),
        ...(beschreibung.trim() ? { beschreibung: beschreibung.trim() } : {}),
      });
      setzeDialogOffen(false);
      setzeName("");
      setzeFaelligAm("");
      setzeBeschreibung("");
    } catch (e) {
      setzeFehler(fehlerMeldung(e));
    }
  }

  return (
    <div>
      <Seitenkopf
        kontext={p ? `${p.name} · ${ohneArchiv.length} ${ohneArchiv.length === 1 ? "Meilenstein" : "Meilensteine"}` : undefined}
      >
        Meilensteine
      </Seitenkopf>

      <Zahlenband className="mt-7">
        <Kennzahl
          label={naechster ? `Tage bis ${naechster.name}` : "Tage bis"}
          grad={56}
          wert={tage != null ? String(tage) : "—"}
          ton={tage != null && tage <= 7 ? "akzent" : undefined}
        />
        <div className="flex min-w-[300px] flex-1 flex-wrap gap-x-[22px] gap-y-1.5 pb-2">
          <Nebenwert wert={ohneArchiv.length} label="Meilensteine" />
          <Nebenwert wert={laufend} label="laufend" />
        </div>
      </Zahlenband>

      {ohneArchiv.length === 0 ? (
        <div className="mt-14 max-w-[52ch]">
          <p className="text-[15.5px] font-light leading-[1.7] text-tinte2">
            Noch keine Meilensteine. Ein Meilenstein bündelt Aufgaben mit Ziel
            und Frist — er gibt dem Projekt einen nächsten Halt.
          </p>
          <Button
            className="mt-7"
            onClick={() => setzeDialogOffen(true)}
            data-testid="meilenstein-neu"
          >
            Meilenstein anlegen
          </Button>
        </div>
      ) : (
        <>
          {/* Erst zeichnen, wenn Fristen und Einstellung da sind — sonst
              stünde für einen Moment ein Ausschnitt da, den die Daten gleich
              widerrufen. */}
          {fenster &&
          !aufgaben.isLoading &&
          !zeitraumEinstellung.isLoading ? (
            <div className="mt-12" data-testid="meilenstein-zeitband">
              <MeilensteinZeitband spannen={spannen} fenster={fenster} />
              <Mikrolabel grad={10.5} className="mt-1 block">
                {fensterText(fenster)} ·{" "}
                {zeitraum === "automatisch"
                  ? "Ausschnitt folgt den Fristen der aufgeplanten Aufgaben"
                  : `fester Ausschnitt (${ZEITSTRAHL_ZEITRAUM_LABEL[zeitraum]})`}
              </Mikrolabel>
            </div>
          ) : null}

          <div className="mt-12">
            <Rubrik
              anzahl={`${ohneArchiv.length} ${ohneArchiv.length === 1 ? "Meilenstein" : "Meilensteine"}`}
              aktion={
                <Button
                  variant="secondary"
                  className="h-[29px] px-3 font-mono text-[11.5px]"
                  onClick={() => setzeDialogOffen(true)}
                >
                  Meilenstein anlegen
                </Button>
              }
            >
              Alle nach Frist
            </Rubrik>
            {ohneArchiv.map((m) => (
              <MeilensteinZeile
                key={m.id}
                meilenstein={m}
                projektId={projektId}
              />
            ))}
          </div>
        </>
      )}

      <Dialog open={dialogOffen} onOpenChange={setzeDialogOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Meilenstein anlegen</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="meilenstein-name">Name</Label>
              <Input
                id="meilenstein-name"
                className="mt-1"
                value={name}
                onChange={(e) => setzeName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="meilenstein-faellig">Fällig am</Label>
              <Input
                id="meilenstein-faellig"
                type="date"
                className="mt-1"
                value={faelligAm}
                onChange={(e) => setzeFaelligAm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="meilenstein-beschreibung">Beschreibung</Label>
              <Input
                id="meilenstein-beschreibung"
                className="mt-1"
                value={beschreibung}
                onChange={(e) => setzeBeschreibung(e.target.value)}
              />
            </div>
            {fehler ? <Fehlerzeile meldung={fehler} /> : null}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeDialogOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void speichern()}
              disabled={!name.trim() || !faelligAm || anlegen.isPending}
            >
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
